import * as React from 'react';

import { useQuery } from '@apollo/client';
import type { EnterpriseContentAssignment_LearnerContentAssignment as AssignedCourse } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';
import User from 'js/lib/user';

import AssignedContentQuery from 'bundles/enterprise-legacy-learner-home/queries/AssignedContent.graphql';
import type {
  AssignedContentQueryVariables,
  AssignedContentQuery as AssignedContentType,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/AssignedContent';
import EnterpriseAssignedCoursesCollectionContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseAssignedCoursesCollectionContext';
import { mapAssignedContentToCollection } from 'bundles/page-config-common/utils';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type Props = {
  children: React.ReactNode;
};

const EnterpriseAssignedCoursesCollectionProvider = ({ children }: Props) => {
  const { programSlug } = useParams();
  const program = useProgram();

  const programId = program?.id;
  const userId = User.get().id;

  const { data, loading } = useQuery<AssignedContentType, AssignedContentQueryVariables>(AssignedContentQuery, {
    variables: {
      userId: String(userId),
      programIds: [programId as string],
      limit: 50,
    },
    ssr: false,
    context: { clientName: 'gatewayGql' },
    skip: !userId || !programId,
  });

  if (!programId || loading) {
    return (
      <EnterpriseAssignedCoursesCollectionContext.Provider
        value={{ isAssignedCoursesCollectionLoading: true, assignedCoursesCollection: undefined }}
      >
        {children}
      </EnterpriseAssignedCoursesCollectionContext.Provider>
    );
  }

  const assignedCourses = data?.EnterpriseContentAssignment?.getAssignmentsByUserAndPrograms?.elements;
  const assignedCoursesCollection = mapAssignedContentToCollection(
    programSlug,
    assignedCourses as AssignedCourse[] | undefined
  );

  return (
    <EnterpriseAssignedCoursesCollectionContext.Provider
      value={{
        isAssignedCoursesCollectionLoading: false,
        assignedCoursesCollection,
      }}
    >
      {children}
    </EnterpriseAssignedCoursesCollectionContext.Provider>
  );
};

export default EnterpriseAssignedCoursesCollectionProvider;
